import type { ZudokuConfig } from "zudoku";
import {
  CrispChat,
  FooterLinks,
  LanguageMenu,
  WebsiteLink,
} from "./src/site-chrome";

const guides = (
  prefix: string,
  label: string,
  section: string,
  pages: Array<[string, string]>,
  reference: { to: string; label: string },
) => ({
  type: "category" as const,
  label,
  link: `${prefix}introduction`,
  items: [
    {
      type: "category" as const,
      label: section,
      items: pages.map(([file, pageLabel]) => ({
        type: "doc" as const,
        file: `${prefix}${file}`,
        label: pageLabel,
      })),
    },
    { type: "link" as const, to: reference.to, label: reference.label },
  ],
});

const config: ZudokuConfig = {
  site: {
    title: "Atomicat API",
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "Atomicat",
      width: "156px",
      href: "/",
    },
    showPoweredBy: false,
    footer: {
      position: "start",
    },
  },
  header: {
    placements: {
      search: "center",
    },
  },
  search: {
    type: "pagefind",
  },
  slots: {
    "head-navigation-start": WebsiteLink,
    "head-navigation-end": LanguageMenu,
    "mobile-top-bar-end": LanguageMenu,
    "footer-before": FooterLinks,
    "layout-after-head": CrispChat,
  },
  metadata: {
    title: "Atomicat API",
    description:
      "Build with the Atomicat API. Pages, sites, funnels, videos, products, leads, and webhooks.",
    favicon: "/favicon.svg",
    applicationName: "Atomicat API",
  },
  navigation: [
    guides("", "English", "Guides", [
      ["introduction", "Introduction"],
      ["authentication", "Authentication"],
      ["requests", "Requests"],
      ["webhooks", "Webhooks"],
      ["changelog", "Changelog"],
    ], { to: "/api", label: "API reference" }),
    guides("es/", "Español", "Guías", [
      ["introduction", "Introducción"],
      ["authentication", "Autenticación"],
      ["requests", "Solicitudes"],
      ["webhooks", "Webhooks"],
      ["changelog", "Cambios"],
    ], { to: "/es/api", label: "Referencia de la API" }),
    guides("pt/", "Português", "Guias", [
      ["introduction", "Introdução"],
      ["authentication", "Autenticação"],
      ["requests", "Requisições"],
      ["webhooks", "Webhooks"],
      ["changelog", "Mudanças"],
    ], { to: "/pt/api", label: "Referência da API" }),
  ],
  redirects: [
    { from: "/", to: "/introduction" },
    { from: "/es", to: "/es/introduction" },
    { from: "/pt", to: "/pt/introduction" },
  ],
  apis: [
    { type: "file", input: "./apis/openapi.json", path: "/api" },
    { type: "file", input: "./apis/openapi.es.json", path: "/es/api" },
    { type: "file", input: "./apis/openapi.pt.json", path: "/pt/api" },
  ],
  defaults: {
    apis: {
      examplesLanguage: "shell",
      schemaDownload: { enabled: true },
    },
  },
  docs: {
    files: "/pages/**/*.{md,mdx}",
    publishMarkdown: true,
    llms: {
      llmsTxt: true,
      llmsTxtFull: true,
    },
  },
  theme: {
    customCss: `
      :root { --top-nav-height: 0px; }
      header > div:last-child { display: none !important; }
      footer > div { padding-top: 3.5rem !important; padding-bottom: 2.5rem !important; }
      footer > div > div:last-child { display: none !important; }
    `,
    light: {
      background: "#ffffff",
      foreground: "#16161d",
      card: "#ffffff",
      cardForeground: "#16161d",
      popover: "#ffffff",
      popoverForeground: "#16161d",
      primary: "#6d4dff",
      primaryForeground: "#ffffff",
      secondary: "#f4f3fb",
      secondaryForeground: "#16161d",
      muted: "#f6f5f8",
      mutedForeground: "#5c5c66",
      accent: "#f3f0ff",
      accentForeground: "#3b2a86",
      border: "#e7e5ee",
      input: "#e7e5ee",
      ring: "#6d4dff",
      radius: "0.75rem",
    },
    dark: {
      background: "#0e0e12",
      foreground: "#f4f3ee",
      card: "#16161c",
      cardForeground: "#f4f3ee",
      popover: "#16161c",
      popoverForeground: "#f4f3ee",
      primary: "#b7a6ff",
      primaryForeground: "#1a1033",
      secondary: "#1c1c24",
      secondaryForeground: "#f4f3ee",
      muted: "#1c1c24",
      mutedForeground: "#a3a3ad",
      accent: "#241c3d",
      accentForeground: "#e4dcff",
      border: "#2a2a33",
      input: "#2a2a33",
      ring: "#b7a6ff",
      radius: "0.75rem",
    },
  },
  sitemap: {
    siteUrl: "https://docs.atomicat.ai",
  },
};

export default config;
